/**
 * LanguageSwitcher
 * A flag + name dropdown. Works for both platform and restaurant mode.
 * 
 * Props:
 *   mode:     'platform' | 'restaurant'
 *   enabled:  string[]   list of enabled language codes
 *   current:  string     current language code
 *   onChange: fn(code)
 *   compact:  bool       show only flag (no name), default false
 */
import React, { useState, useRef, useEffect } from 'react';
import { LANGUAGES } from '../context/LanguageContext';

export default function LanguageSwitcher({ enabled = ['en'], current = 'en', onChange, compact = false, light = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Don't render if only one language
  if (!enabled || enabled.length <= 1) return null;

  const currentLang = LANGUAGES[current] || LANGUAGES.en;
  const available   = enabled.map(c => LANGUAGES[c]).filter(Boolean);

  return (
    <div className={`ls-wrap ${light ? 'ls-light' : ''}`} ref={ref}>
      <button className="ls-trigger" onClick={() => setOpen(o => !o)} title="Change language">
        <span className="ls-globe" aria-hidden="true">🌐</span>
        <span className="ls-flag">{currentLang.flag}</span>
        {!compact && <span className="ls-name">{currentLang.name}</span>}
        <span className="ls-caret">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="ls-dropdown">
          {available.map(lang => (
            <button
              key={lang.code}
              className={`ls-option ${lang.code === current ? 'active' : ''}`}
              onClick={() => { onChange(lang.code); setOpen(false); }}
            >
              <span className="ls-flag">{lang.flag}</span>
              <span className="ls-name">{lang.name}</span>
              {lang.code === current && <span className="ls-check">✓</span>}
            </button>
          ))}
        </div>
      )}

      <style>{`
        .ls-wrap { position: relative; display: inline-block; font-family: 'DM Sans', sans-serif; }
        .ls-trigger { display: flex; align-items: center; gap: .4rem; padding: .45rem .8rem; border-radius: 20px; border: 1px solid rgba(255,255,255,.15); background: rgba(255,255,255,.07); color: #fff; cursor: pointer; font-size: .82rem; font-family: 'DM Sans', sans-serif; transition: all .2s; white-space: nowrap; }
        .ls-trigger:hover { background: rgba(255,255,255,.13); border-color: rgba(255,255,255,.25); }
        .ls-light .ls-trigger { border-color: rgba(0,0,0,.15); background: rgba(0,0,0,.06); color: #333; }
        .ls-light .ls-trigger:hover { background: rgba(0,0,0,.1); }
        .ls-flag { font-size: 1.1rem; line-height: 1; }
        .ls-name { font-size: .8rem; }
        .ls-caret { font-size: .55rem; opacity: .6; }
        .ls-globe { display: none; font-size: 1.2rem; line-height: 1; }
        /* On phones, collapse to an iPhone-style globe icon only */
        @media (max-width: 600px) {
          .ls-trigger { padding: 0; width: 42px; height: 42px; border-radius: 50%; justify-content: center; }
          .ls-globe { display: inline; }
          .ls-flag, .ls-name, .ls-caret { display: none; }
        }
        .ls-dropdown { position: absolute; top: calc(100% + .4rem); right: 0; background: #1e1e1e; border: 1px solid rgba(255,255,255,.12); border-radius: 14px; padding: .4rem; min-width: 160px; z-index: 500; box-shadow: 0 12px 40px rgba(0,0,0,.5); animation: lsDrop .15s ease; }
        .ls-light .ls-dropdown { background: #fff; border-color: rgba(0,0,0,.12); box-shadow: 0 8px 30px rgba(0,0,0,.15); }
        @keyframes lsDrop { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .ls-option { display: flex; align-items: center; gap: .6rem; width: 100%; padding: .55rem .75rem; border: none; background: none; color: rgba(255,255,255,.75); cursor: pointer; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: .85rem; text-align: left; transition: background .15s; }
        .ls-option:hover { background: rgba(255,255,255,.08); color: #fff; }
        .ls-option.active { color: #c8a96e; font-weight: 700; }
        .ls-light .ls-option { color: #444; }
        .ls-light .ls-option:hover { background: rgba(0,0,0,.06); color: #000; }
        .ls-light .ls-option.active { color: #c8a96e; }
        .ls-check { margin-left: auto; font-size: .8rem; color: #c8a96e; }
      `}</style>
    </div>
  );
}
